import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { Amplify } from 'aws-amplify';
import { generateClient, GraphQLSubscription } from 'aws-amplify/api';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import CardStack from 'components/shared/CardStack/CardStack';
import ClientSlackCard from './ClientSlackCard';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import { HuddleClientService } from 'service/huddle-client.service';
import { SlackService } from 'service/slack.service';
import { onSlackEvent } from 'model/graphql/subscriptions';
import config from 'model/aws-exports';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/slices/toastSlice';
import zeplinColor from 'theme/Color';
import { renderSlackMessage } from './helper/renderSlackMessage';

Amplify.configure(config as any);
const amplifyClient = generateClient();

const CHANNEL_IDS =
  process.env.REACT_APP_ENV === 'prod'
    ? ['general', 'deals', 'recruiting', 'social']
    : ['huddle-grid-testers', 'huddle-grid-testers1'];

const SlackFeed = () => {
  const location = useLocation();

  const dispatch = useAppDispatch();
  const IsSlackConnected = sessionStorage.getItem('isSlackConnected') === 'true';
  const isSlackConnecting = sessionStorage.getItem('slackConnecting') === 'true';
  const slackAuthOrigin = sessionStorage.getItem('slackAuthOrigin') || 'activity';
  const clientPathId = location.pathname.split('/').pop() || '';

  const handleRedirect = () => {
    sessionStorage.setItem('redirect_path', location?.pathname);
    sessionStorage.setItem('currentTab', '4');
    sessionStorage.setItem('slackAuthOrigin', 'communication');
    sessionStorage.setItem('slackConnecting', 'true');
  };

  const [loading, setLoading] = useState(false);
  const [slackLoading, setSlackLoading] = useState(false);
  const [slackMessage, setSlackMessage] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [newDataTotal, setNewDataTotal] = useState(0);
  const [channelID, setChannelID] = useState<string | null>(null);
  const [channelCursors, setChannelCursors] = useState<Record<string, string | null>>({});
  const [subscriptionCount, setSubscriptionCount] = useState<number | null>(null);
  const slackAuthAttemptedRef = useRef(false);

  const slackMessageRef = useRef(slackMessage);

  useEffect(() => {
    slackMessageRef.current = slackMessage;
  }, [slackMessage]);

  useEffect(() => {
    const handleSlackOAuthReturn = async () => {
      if (!isSlackConnecting || slackAuthOrigin !== 'communication' || !location.search) {
        return;
      }

      const code = new URLSearchParams(location.search).get('code') || '';

      // Check the mutable ref instead of state
      if (!code || IsSlackConnected || slackAuthAttemptedRef.current) return;

      try {
        // LOCK IT IMMEDIATELY before the async await call starts
        slackAuthAttemptedRef.current = true;

        const res = await SlackService.getSlackLogin(code.replace('&state=', ''));

        if (res?.ok) {
          sessionStorage.setItem('isSlackConnected', 'true');
          sessionStorage.removeItem('slackConnecting');
          sessionStorage.removeItem('slackAuthOrigin');
          sessionStorage.removeItem('redirect_path');
          sessionStorage.removeItem('sf_redirect');
          window.dispatchEvent(
            new CustomEvent('sessionStorageChange', {
              detail: { key: 'isSlackConnected', value: 'true' },
            }),
          );
          dispatch(showToast({ message: 'Slack connected successfully' }));
          return;
        }

        // If it failed, reset the ref lock so the user can retry if needed
        slackAuthAttemptedRef.current = false;

        dispatch(showToast({ message: 'Failed to connect Slack account', error: true }));
      } catch (error: any) {
        // Reset ref lock on error as well
        slackAuthAttemptedRef.current = false;

        dispatch(
          showToast({
            message: error?.message || 'Failed to connect Slack account',
            error: true,
          }),
        );
      }
    };

    handleSlackOAuthReturn();
  }, [isSlackConnecting]);

  // ── Real-time subscription ──────────────────────────────────────────────
  useEffect(() => {
    console.log('Setting up SlackFeed subscription, IsSlackConnected:', IsSlackConnected);
    const subscription = IsSlackConnected
      ? channelID?.split(',')?.map((id) =>
          amplifyClient
            .graphql<GraphQLSubscription<typeof onSlackEvent>>({
              query: onSlackEvent,
              variables: { channel: id },
              authMode: 'apiKey' as const,
            })
            .subscribe({
              next: (response) => {
                console.log('Received Slack event:', response, id);
                const event = (response.data as any)?.onSlackEvent;
                if (!event || event?.channelId !== id || event?.eventType === 'reaction_added')
                  return;

                if (event?.eventType === 'message deleted') {
                  setSlackMessage((prev) => prev.filter((m) => m.text !== event.text));
                  setEvents((prev) => prev.filter((m) => m.text !== event.text));
                } else if (event?.eventType === 'edited message') {
                  setSlackMessage((prev) => [
                    {
                      ...event,
                      userDetails: { name: event?.user, image: event?.userImage },
                      text: renderSlackMessage(event),
                    },
                    ...prev.filter((m) => m?.text !== event?.originalText),
                  ]);
                } else {
                  setSlackMessage((prev) => [
                    {
                      ...event,
                      userDetails: { name: event?.user, image: event?.userImage },
                      text: renderSlackMessage(event),
                    },
                    ...prev,
                  ]);
                  setEvents((prev) => [
                    {
                      ...event,
                      userDetails: { name: event?.user, image: event?.userImage },
                      text: renderSlackMessage(event),
                    },
                    ...prev,
                  ]);
                }
              },
              error: (err: Error) => console.error('SlackFeed subscription error:', err),
            }),
        )
      : undefined;

    return () => subscription?.forEach((sub) => sub.unsubscribe());
  }, [IsSlackConnected, channelID]);

  // ── Fetch messages for a single channel ────────────────────────────────
  const getSlackMessage = async (channelId: string) => {
    try {
      setSlackLoading(true);
      const res = await SlackService.getSlackChannelMessages(channelId);
      setChannelCursors((prev) => ({ ...prev, [channelId]: res?.next_cursor || null }));
      const filtered = (res?.messages || []).filter(
        (m: any) => m?.user !== 'USLACKBOT' && m?.sender !== 'USLACKBOT',
      );
      const withMeta = filtered.map((m: any) => ({
        ...m,

        channelId,
        channel: res?.channelName,
      }));
      setSlackMessage((prev) => [...prev, ...withMeta]);
    } catch (err) {
      console.error('SlackFeed getSlackMessage error:', err);
    } finally {
      setSlackLoading(false);
    }
  };

  // ── Resolve client → slackChannelId then fetch ─────────────────────────
  const loadSlackMessages = async (refresh = false) => {
    setLoading(true);
    try {
      const res = await HuddleClientService.getAllClient();
      const currentAccountId = clientPathId;
      if (currentAccountId && res?.clients) {
        const match = res.clients.find(
          (c: any) => c.AccountId === currentAccountId || c.Id === currentAccountId,
        );

        if (IsSlackConnected) {
          refresh && setSlackMessage([]);
          const subscribedChannelIds = (match?.slackChannelId || '')
            .split(',')
            .map((id: string) => id.trim())
            .filter(Boolean);

          setSubscriptionCount(subscribedChannelIds.length);

          if (subscribedChannelIds.length > 0) {
            setChannelID(subscribedChannelIds.join(','));
            subscribedChannelIds.forEach((id: string) => getSlackMessage(id));
          } else {
            setChannelID(null);
          }
        }
      }
    } catch (err) {
      console.error('SlackFeed loadSlackMessages error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (IsSlackConnected) loadSlackMessages(true);
  }, [IsSlackConnected, clientPathId]);

  // ── Actions ────────────────────────────────────────────────────────────
  const handleRefresh = async () => {
    setEvents([]);
    await loadSlackMessages(true);
  };

  const handleMarkRead = () => {
    setNewDataTotal(0);
    setEvents([]);
  };

  // ── Date grouping ──────────────────────────────────────────────────────
  const toDateLabel = (ts: string | undefined, date?: string | Date) => {
    const d = ts ? new Date(parseInt(ts.split('.')[0]) * 1000) : date ? new Date(date) : new Date();
    return d.toLocaleDateString();
  };

  const todayLabel = new Date().toLocaleDateString();
  const yesterdayLabel = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toLocaleDateString();
  })();

  const grouped = useMemo(() => {
    const result: { today: any[]; yesterday: any[]; otherDates: Record<string, any[]> } = {
      today: [],
      yesterday: [],
      otherDates: {},
    };
    slackMessage.forEach((msg) => {
      if (msg?.subtype) return;
      const label = toDateLabel(msg?.ts, msg?.date);
      if (label === todayLabel) result.today.push(msg);
      else if (label === yesterdayLabel) result.yesterday.push(msg);
      else {
        if (!result.otherDates[label]) result.otherDates[label] = [];
        result.otherDates[label].push(msg);
      }
    });
    return result;
  }, [slackMessage]);

  // ── Render a group of Slack messages ──────────────────────────────────
  const renderGroup = (messages: any[]) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {messages.map((val: any, ind: number) => {
        if (val?.subtype) return null;
        const cards: any[] = [
          {
            ...val,
            new: events?.some((e) => e?.ts === val?.ts && e?.text === val?.text),
            id: `slack_${val.user}_${val.ts}`,
            channel: val?.channel,
            isSlack: true,
            sender: val?.userDetails?.name,
            sender_img: val?.userDetails?.image,
            account: val?.text,
            date: val.ts ? new Date(parseInt(val.ts.split('.')[0]) * 1000) : new Date(),
          },
        ];
        if (Array.isArray(val.replies)) {
          val.replies.forEach((reply: any) => {
            cards.push({
              ...reply,
              id: `slack_reply_${reply.user}_${reply.ts}`,
              new: events?.some((e) => e?.ts === val?.ts && e?.text === val?.text),
              isSlack: true,
              isReply: true,
              parentMessage: renderSlackMessage(val),
              channelId: val.channelId,
              thread_ts: val.ts || val.thread_ts,
              sender: reply?.userDetails?.name,
              sender_img: reply?.userDetails?.image,
              account: renderSlackMessage(reply),
              date: reply.ts ? new Date(parseInt(reply.ts.split('.')[0]) * 1000) : new Date(),
            });
          });
        }
        return (
          <Box
            key={`slack_${val.user}_${val.ts}_${ind}`}
            sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <CardStack
              cards={cards}
              renderCard={(
                card,
                isExpanded,
                _isLast,
                isFirst,
                total,
                handleCardClick,
                onReplyChange,
              ) => (
                <ClientSlackCard
                  message={card}
                  newDataTotal={newDataTotal}
                  index={ind}
                  isExpanded={isExpanded}
                  isFirstCard={isFirst}
                  total={total}
                  handleCardClick={handleCardClick}
                  onReplyChange={onReplyChange}
                />
              )}
            />
          </Box>
        );
      })}
    </Box>
  );

  const isEmpty =
    !loading &&
    !slackLoading &&
    grouped.today.length === 0 &&
    grouped.yesterday.length === 0 &&
    Object.keys(grouped.otherDates).length === 0;

  const iconSx = {
    borderRadius: '50px',
    width: { xs: '28px', xl: '48px' },
    height: { xs: '28px', xl: '48px' },
    border: `1px solid ${zeplinColor.Neutral6}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
          Internal
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <LightTooltip
            title={
              <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                Refresh
              </Typography>
            }
            placement='bottom-end'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
          >
            <Box sx={iconSx} onClick={handleRefresh}>
              <img
                src='/assets/refresh.svg'
                alt='refresh'
                style={{ width: '20px', height: '20px' }}
              />
            </Box>
          </LightTooltip>

          <LightTooltip
            title={
              <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                Mark all as read
              </Typography>
            }
            placement='bottom-end'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
          >
            <Box sx={iconSx} onClick={handleMarkRead}>
              <img
                src='/assets/done_all.svg'
                alt='mark-read'
                style={{ width: '20px', height: '20px' }}
              />
            </Box>
          </LightTooltip>
        </Box>
      </Box>

      {/* New messages badge */}
      {newDataTotal + events?.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Chip
            label={
              <Typography variant='responsiveLabel1' color={zeplinColor.BasicBlack}>
                {newDataTotal + events?.length} new{' '}
                {newDataTotal + events?.length > 1 ? 'messages' : 'message'}
              </Typography>
            }
            sx={{
              backgroundColor: zeplinColor.Neutral3,
              padding: '5px 12px',
              borderRadius: '18px',
            }}
          />
        </Box>
      )}

      {/* Feed */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          minHeight: 0,
        }}
      >
        {!IsSlackConnected ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '12px',
              border: `1px solid ${zeplinColor.Neutral5}`,
              borderRadius: '12px',
              backgroundColor: zeplinColor.Neutral1,
            }}
          >
            <Typography variant='responsiveHeading4'>Connect Slack to Get Started</Typography>
            <Typography variant='responsiveBody3'>
              Connect your Slack for real-time messaging, notifications and collaboration within
              Huddle
            </Typography>
            <Link
              to={`https://aamanagementgroup.slack.com/oauth/v2/authorize?${queryString.stringify({
                client_id: '1547360932450.9088642262659',
                scope: '',
                user_scope:
                  'identify,users:read,channels:read,channels:history,groups:read,groups:write,chat:write,groups:history,im:read,im:history,mpim:read,mpim:history,reactions:read,reactions:write,emoji:read',
                redirect_uri: `${process.env.REACT_APP_REDIRECT}`,
                team: 'T01G3ALTED8',
              })}`}
              style={{ textDecoration: 'none', width: 'fit-content' }}
              onClick={handleRedirect}
            >
              <CustomButton
                disabled={false}
                onClick={() => {}}
                variant='contained'
                color='primary'
                buttonVariant='buttonSmall'
                style={{
                  borderRadius: '8px',
                  height: '32px',
                  padding: '6px 12px',
                  '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                }}
                textStyle={{ color: zeplinColor.Neutral1 }}
                text='Connect'
              />
            </Link>
          </Box>
        ) : loading || (slackLoading && slackMessage.length === 0) ? (
          [1, 2, 3, 4].map((_, i) => <Skeleton key={i} variant='rounded' height={80} />)
        ) : isEmpty ? (
          /* Demo preview — replace with empty state once real data flows */
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              height: 'calc(100vh - 500px)',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            {subscriptionCount === 0 ? (
              <>
                <Typography variant='responsiveHeading4'>
                  You haven't subscribed to any channels yet.
                </Typography>
              </>
            ) : (
              <Typography variant='responsiveHeading4'>
                No new messages in your subscribed channels right now.
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {grouped.today.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography variant='responsiveHeading4'>Today</Typography>
                {renderGroup(grouped.today)}
              </Box>
            )}
            {grouped.yesterday.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography variant='responsiveHeading4'>Yesterday</Typography>
                {renderGroup(grouped.yesterday)}
              </Box>
            )}
            {Object.keys(grouped.otherDates).map((date) => (
              <Box key={date} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography variant='responsiveHeading4'>{date}</Typography>
                {renderGroup(grouped.otherDates[date])}
              </Box>
            ))}
          </>
        )}
      </Box>
    </>
  );
};

export default SlackFeed;
